<script>
	import { fade } from 'svelte/transition';

	import 'bulma/css/bulma.min.css';
	import 'bulma-slider/dist/css/bulma-slider.min.css';
	import 'bulma-checkradio/dist/css/bulma-checkradio.min.css';

	import InputPanel from "./InputPanel.svelte";
	// import RadarChart from "./RadarChart.svelte";
	import RiskBar from "./RiskBar.svelte";
	import ShowChanges from "./ShowChanges.svelte";

	let state = 'FIRST_RUN';

	let values = {};
	let frozenValues = {};

	let show_info = false;

	const DEBUG = false;

	function showInfo(e) {
		show_info = e.target.id;
	}
</script>

<main class="container">
	<div class="is-pulled-right is-hidden-print" style="padding-top: 1.5em">
		<a class="is-link" id="help" on:click={showInfo}>Help</a>
	</div>
	<h1 id="title" class="mb-4">
		Peri-implantitis risk assessment tool*
	</h1>

	<div class="columns">
		<div class="column is-5">
			<InputPanel bind:state={state} bind:info={show_info}
						bind:values={values} bind:frozenValues={frozenValues} />
		</div>

		<div class="column is-4">
			{#if (state === 'CALCULATED')}
			<div transition:fade>
				<div id="risk-bar" on:mouseenter={showInfo}>
					<RiskBar values={values} frozenValues={frozenValues} DEBUG={DEBUG} />
				</div>

				<div class="is-flex is-justify-content-space-between mt-1 has-text-weight-bold">
					<div>Lower risk</div>
					<div>Higher risk</div>
				</div>

				<div id="changes" on:mouseenter={showInfo}>
					<ShowChanges values={values} frozenValues={frozenValues} />
				</div>
			</div>
			{/if}
		</div>

		<div class="column is-3 is-hidden-print">
			<div class="content">
				{#if show_info === "smoking"}
					<div in:fade>
						<h1>Smoking</h1>

						<p>Enter the number of cigarettes the patient smokes per day. For non-smokers, enter zero.</p>
					</div>
				{:else if show_info === "recall"}
					<div in:fade>
						<h1>Recall compliance</h1>

						<p>Enter the average number of months between recall appointments for this patient.</p>
					</div>
				{:else if show_info === "systemic"}
					<div in:fade>
						<h1>General health</h1>

						<p>Select systemic disease if the patient has uncontrolled diabetes.</p>
					</div>
				{:else if show_info === "location"}
					<div in:fade>
						<h1>Implant location</h1>

						<p>Select which jaw the implant(s) will be placed in.</p>
					</div>
				{:else if show_info === "age"}
					<div in:fade>
						<h1>Age</h1>

						<p>Enter the age of the patient, in years.</p>
					</div>
				{:else if show_info === "edentulism"}
					<div in:fade>
						<h1>Edentulism</h1>

						<p>At this stage, this tool can only be used for partially edentulous cases, as we do not
						have sufficient data to build a predictive model for fully edentulous patients.</p>
					</div>
				{:else if show_info === "fmbs"}
					<div in:fade>
						<h1>Full mouth bleeding score</h1>

						<p>Enter the full mouth bleeding score for the patients, that is the percentage of sites that display
						bleeding on probing.</p>
					</div>
				{:else if show_info === "ppd"}
					<div in:fade>
						<h1>Number of teeth with PPD &geq; 5 mm</h1>

						<p>Enter the number of teeth which have a pocket probing depth greater than or equal to 5 mm.</p>
					</div>
				{:else if show_info === "perio"}
					<div in:fade>
						<h1>History of periodontitis</h1>

						<p>Enter whether this patient has previously been diagnosed with periodontitis.</p>
					</div>
				{:else if show_info === "missing"}
					<div in:fade>
						<h1>Teeth lost</h1>

						<p>Enter the number of teeth the patient has lost (not counting wisdom teeth).</p>
					</div>
				{:else if show_info === "boneloss"}
					<div in:fade>
						<h1>Alveolar bone loss</h1>

						<p>Enter the percentage of bone loss around the most affected tooth.
							For example, if one third of the root of the most affected tooth is no longer in bone,
							enter 33%.
						</p>
					</div>
				{:else if show_info === "calculate-button"}
					<div in:fade>
						<h1>Calculate risk</h1>

						<p>Once you have filled in all the fields above, click the 'Calculate' button to see
						this patient's risk.
						</p>

						<p>
							Afterwards, you can still change the modifiable risk factors and see the effect
							that would have on the total risk of the patient.
						</p>
					</div>
				{:else if show_info === "reset-button"}
					<div in:fade>
						<h1>Reset</h1>

						<p>Click to clear all the values and start a new risk assessment.</p>
					</div>
				{:else if show_info === "radar-chart"}
					<div in:fade>
						<h1>Radar chart</h1>

						<p>Not implemented yet.</p>
					</div>
				{:else if show_info === "risk-bar"}
					<div in:fade>
						<h1>Risk bar</h1>

						<p>
							The black marker shows the current risk score of the patient.
							If you change any of the modifiable risk factors, a grey marker
							will appear indicating the potential risk score.
						</p>
					</div>
				{:else if show_info === "changes"}
					<div in:fade>
						<h1>Changes</h1>

						<p>These are proposed changes to the modifiable risk factors.</p>
					</div>
				{:else}
					<div in:fade>
						<h1>Welcome</h1>

						<p>You can use this tool to pre-operatively determine the risk for developing peri-implantitis
							in your patients. No patient information is recorded.
						</p>

						<p>
							At the moment, the tool only supports the assessment of partially edentulous cases, but we
							are working on developing a model for fully edentulous patients as well. If you are a
							clinician or researcher at a university clinic and would like to help, please contact
							<a href="mailto:marc.quirynen@gmail.com">marc.quirynen@gmail.com</a>.
						</p>

						<p>You can click on <b>Help</b> in the top right corner to see this message again.</p>

						<p class="is-size-7">
							&copy; {new Date().getFullYear()} All rights reserved.<br>
							Periodontology &amp; Oral Microbiology,<br>
							Department of Oral Health Sciences,<br>
							KU Leuven, Belgium.
						</p>

						<p class="is-size-7">
							Based on model v1.0-2022 published in
							<em>Quirynen M, Tarce M, Siawasch M, Castro AB, Temmerman A, Coucke W, Teughels W. Peri-implantitis Risk Assessment (PiRA) Part 2: Retrospective Study and Framework for an Evidence-Based Prediction Model for Clinicians. Int J Oral Maxillofac Implants. 2025 Sep 26;40(5):571-578. doi: 10.11607/jomi.11211. PMID: 40512527</em>.
						</p>

						<p class="is-size-7">
							* To emphasize the relative impact of each of the factors, we have slightly modified their importance, based on odds ratios of existing systematic reviews (Tarce et al. 2025).
						</p>

						<p class="is-size-7">
						</p>
					</div>
				{/if}
			</div>
		</div>
	</div>

{#if (DEBUG)}
	<pre style="font-size: 7pt">
Smoking [cigs/day]: {values.smoking}
Recall [months]: {values.recall}
General health (Yes): {values.systemic}
Location (Upper jaw): {values.location === "upper"}

Edentulism: {values.edentulism}
	# teeth lost: {values.teeth_lost}
	FMBS [%]: {values.fmbs}
	# PPD >= 5mm: {values.ppd}

	History of perio: {values.perio}
		Age: {values.age}
		Bone loss: {values.bone_loss}
		Susceptibility [%]: {values.bone_loss / values.age}
</pre>
{/if}
</main>

<style>
	h1#title {
		color: #ff3e00;
		text-transform: uppercase;
		font-size: 3em;
		font-weight: 100;
		line-height: 3.5rem;
	}

	@media print {
		.is-hidden-print {
			display: none !important;
		}
	}
</style>